// ── V153: 반제품 이름이 식재료와 겹칠 때 — 레시피 화면의 경고 + 한 번에 고치기 ───────────
// 예: 반제품 '나쵸치즈'(소스)가 식재료 '나쵸치즈'(사 온 치즈)를 재료로 쓴다 → 셈핏이 둘을 같은 것으로 본다.
import React, { useMemo, useState } from 'react';
import { useLang } from '../i18n/LangContext';
import { semiClashOf, suggestSemiName, josa } from '../utils/semiClash';
import { sameIngredientName } from '../utils/ingredientMatch';

const CATS = ['공산품', '신선식품', '소모품'];
const CAT_ZH = { 공산품: '干货', 신선식품: '生鲜', 소모품: '耗材' };

export default function SemiClashCard({ item, allRecipes = [], prices = [], onFix, lastOrderOf }) {
  const { lang, fmt: money } = useLang();
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);
  const clash = useMemo(() => semiClashOf(item, allRecipes, prices), [item, allRecipes, prices]);
  const [newName, setNewName] = useState(() => suggestSemiName(item?.name, allRecipes, prices));
  const [cat, setCat] = useState('공산품');
  const [price, setPrice] = useState('');
  const [netG, setNetG] = useState('');
  const [sw, setSw] = useState({});
  const [done, setDone] = useState(false);
  if (!clash || !onFix) return null;

  const name = item.name;
  const overRow = clash.overwritten ? clash.semiRows[0].p : null;
  const last = clash.overwritten && lastOrderOf ? lastOrderOf(name) : null;
  const n = newName.trim();
  const nameTaken = !!n && ((allRecipes || []).some(r => r && sameIngredientName(r.name, n)) || (prices || []).some(p => p && sameIngredientName(p.name, n)));
  const canFix = !!n && !nameTaken && !sameIngredientName(n, name);
  const inp = { padding: '6px 9px', borderRadius: 7, border: '1.5px solid #FDBA74', fontSize: 13, background: '#fff' };

  const apply = () => {
    if (!canFix) return;
    const ok = onFix({
      recipeId: item.id, newName: n,
      restore: clash.overwritten ? { category: cat, price: price === '' ? '' : Number(price), net_g: Number(netG) || 0 } : null,
      switchIds: Object.keys(sw).filter(k => sw[k]),
    });
    if (ok !== false) setDone(true);
  };
  if (done) return null;

  return (
    <div data-semi-clash style={{ background: '#FFF7ED', border: '1.5px solid #FB923C', borderRadius: 12, padding: '14px 16px', marginBottom: 18, fontSize: 13, color: '#7C2D12', lineHeight: 1.6 }}>
      <div style={{ fontSize: 14, fontWeight: 900, marginBottom: 4 }}>
        ⚠ {Z(`반제품 이름 ${josa(`'${name}'`, '이/가')} 식재료 ${josa(`'${name}'`, '과/와')} 같아요`, `半成品名「${name}」和食材「${name}」重名`)}
      </div>
      <div style={{ fontSize: 12.5, color: '#9A3412' }}>
        {Z('이름이 같으면 셈핏이 둘을 같은 것으로 봐요:', '名字一样，SEMFIT 会把两个当成同一个：')}
        <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
          {clash.selfRef && <li>{Z('이 반제품 원가가 자기 자신을 재료로 계산돼요', '这个半成品的成本会把自己当成食材来算')}</li>}
          <li>{Z('다른 레시피에서 반제품으로 고를 수 없어요', '在别的配方里选不到这个半成品')}</li>
          <li>{Z('단가표에서 식재료 이름을 바꾸면 반제품 이름도 같이 바뀌어요', '在单价表改食材名字，半成品名字也会跟着变')}</li>
        </ul>
      </div>

      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong>{Z('반제품 새 이름', '半成品新名字')}</strong>
        <input data-clash-name value={newName} onChange={e => setNewName(e.target.value)} style={{ ...inp, minWidth: 200 }} />
        {nameTaken && <span style={{ color: '#DC2626', fontSize: 12 }}>{Z('이미 있는 이름이에요', '这个名字已经有了')}</span>}
      </div>
      <div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>
        {Z(`이 반제품 안의 재료 ${josa(`'${name}'`, '은/는')} 식재료 그대로 둬요.`, `这个半成品里的食材「${name}」保持不变。`)}
      </div>

      {clash.overwritten && (
        <div data-clash-restore style={{ marginTop: 10, background: '#fff', border: '1px solid #FED7AA', borderRadius: 10, padding: '10px 12px' }}>
          <div style={{ fontWeight: 800 }}>
            {Z(`단가표의 '${name}' 줄이 반제품 원가로 덮여 있어요`, `单价表里「${name}」这一行被半成品成本盖掉了`)}
            <span style={{ fontWeight: 500, color: '#9A3412' }}> ({Z('지금', '现在')} {money(overRow.price || 0)} / {overRow.net_g || 0}g)</span>
          </div>
          <div style={{ fontSize: 12, color: '#9A3412', marginBottom: 6 }}>
            {Z('사 온 식재료의 원래 단가와 중량을 넣어 주세요. 비워 두면 나중에 단가표에서 넣어도 돼요.', '请填写买来的食材原来的单价和重量。也可以先空着，之后在单价表里填。')}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select data-clash-cat value={cat} onChange={e => setCat(e.target.value)} style={inp}>
              {CATS.map(c => <option key={c} value={c}>{lang === 'zh' ? CAT_ZH[c] : c}</option>)}
            </select>
            <input data-clash-price type="number" value={price} onChange={e => setPrice(e.target.value)} placeholder={Z('단가', '单价')} style={{ ...inp, width: 110 }} />
            <input data-clash-net type="number" value={netG} onChange={e => setNetG(e.target.value)}
              placeholder={cat === '신선식품' ? Z('구매 중량 (g)', '购买重量 (g)') : cat === '공산품' ? Z('NET 중량 (g)', '净含量 (g)') : Z('기준량 (g)', '基准量 (g)')} style={{ ...inp, width: 130 }} />
          </div>
          {last && (
            <div data-clash-last style={{ fontSize: 12, color: '#52525B', marginTop: 6 }}>
              🧾 {Z('최근 발주', '最近订货')}: {last.date} · {money(last.price)}{last.unit ? ` / ${last.unit}` : ''}{last.spec ? ` · ${last.spec}` : ''}
            </div>
          )}
        </div>
      )}

      {clash.usedIn.length > 0 && (
        <div data-clash-used style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {Z(`다른 레시피에서 ${josa(`'${name}'`, '을/를')} 쓰는 곳 — 반제품(소스 등)을 넣으려던 곳만 체크하세요`, `别的配方里用到「${name}」的地方 —— 只勾选本来想用半成品的`)}
          </div>
          <div style={{ fontSize: 12, color: '#9A3412' }}>{Z('체크 안 한 곳은 식재료 그대로예요.', '没勾选的保持食材不变。')}</div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            {clash.usedIn.map(r => (
              <label key={r.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fff', border: '1px solid #FED7AA', borderRadius: 8, padding: '4px 9px', cursor: 'pointer' }}>
                <input type="checkbox" data-clash-switch={r.id} checked={!!sw[r.id]} onChange={e => setSw(s => ({ ...s, [r.id]: e.target.checked }))} />
                {lang === 'zh' ? (r.name_zh || r.name) : r.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <button type="button" data-clash-fix disabled={!canFix} onClick={apply}
        style={{ marginTop: 12, padding: '9px 16px', borderRadius: 9, border: 'none', fontSize: 14, fontWeight: 800,
                 background: canFix ? '#EA580C' : '#E4E4E7', color: '#fff', cursor: canFix ? 'pointer' : 'default' }}>
        🔧 {Z('이름 바꾸고 고치기', '改名并修好')}
      </button>
    </div>
  );
}
